import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Button, Table, Thead, Tbody, Tr, Th, Td, HStack, useToast, Spinner, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel, Input, Select, useDisclosure, Stack, Flex, Text
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { supabase } from '../../services/supabaseClient';
import AdminLayout from './AdminLayout';

// Função utilitária para formatar telefone BR
function formatPhoneBR(value) {
  if (!value) return '';
  // Remove tudo que não for número
  value = value.replace(/\D/g, '');
  if (value.length <= 10) {
    // Formato fixo ou celular antigo: (99) 9999-9999
    return value.replace(/(\d{2})(\d{4})(\d{0,4})/, function (_, ddd, parte1, parte2) {
      return `(${ddd}) ${parte1}${parte2 ? '-' + parte2 : ''}`;
    });
  } else {
    // Celular novo: (99) 99999-9999
    return value.replace(/(\d{2})(\d{5})(\d{0,4})/, function (_, ddd, parte1, parte2) {
      return `(${ddd}) ${parte1}${parte2 ? '-' + parte2 : ''}`;
    });
  }
}

const initialForm = {
  nome: '',
  telefone: '',
  data_inicio: '',
  data_fim: '',
  status: 'ativo',
  barbeiro_id: '',
  foto_url: '',
  pacote_id: ''
};

// Função utilitária para obter data local em formato yyyy-mm-dd
function getHojeLocalISO() {
  const hoje = new Date();
  hoje.setMinutes(hoje.getMinutes() - hoje.getTimezoneOffset());
  return hoje.toISOString().slice(0, 10);
}
function getFimLocalISO(dias = 30) {
  const fim = new Date();
  fim.setMinutes(fim.getMinutes() - fim.getTimezoneOffset());
  fim.setDate(fim.getDate() + dias);
  return fim.toISOString().slice(0, 10);
}

// Função utilitária para formatar data ISO yyyy-mm-dd para dd/mm/yyyy
function formatarDataBR(dataIso) {
  if (!dataIso) return '';
  const [ano, mes, dia] = dataIso.split('-');
  return `${dia}/${mes}/${ano}`;
}

// Função utilitária para formatar telefone padrão BR
function formatTelefoneBR(telefone) {
  if (!telefone) return '';
  // Remove tudo que não for número
  const num = telefone.replace(/\D/g, '');
  if (num.length === 11) {
    // Celular com DDD: (99) 99999-9999
    return `(${num.slice(0, 2)}) ${num.slice(2, 7)}-${num.slice(7, 11)}`;
  } else if (num.length === 10) {
    // Telefone fixo com DDD: (99) 9999-9999
    return `(${num.slice(0, 2)}) ${num.slice(2, 6)}-${num.slice(6, 10)}`;
  }
  return telefone; // fallback
}

const MensalistasPage = () => {
  const [mensalistas, setMensalistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [fotoFile, setFotoFile] = useState(null);
  const [setUploadingFoto] = useState(false);
  const [barbeiros, setBarbeiros] = useState([]);
  const [pacotes, setPacotes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [renovarModal, setRenovarModal] = useState({ isOpen: false, mensalista: null, dataInicio: '', dataFim: '' });
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Handler para abrir modal de renovação
  const handleRenovarClick = (mensalista) => {
    const hojeStr = getHojeLocalISO();
    const fimStr = getFimLocalISO(30);
    setRenovarModal({ isOpen: true, mensalista, dataInicio: hojeStr, dataFim: fimStr });
  };


  // Handler para confirmar renovação
  const handleConfirmRenovar = async () => {
    const { mensalista, dataInicio, dataFim } = renovarModal;
    const { error } = await supabase.from('mensalistas').update({
      status: 'ativo',
      data_inicio: dataInicio,
      data_fim: dataFim,
    }).eq('id', mensalista.id);
    if (!error) {
      toast({ title: 'Plano renovado com sucesso!', status: 'success' });
      fetchMensalistas();
    } else {
      toast({ title: 'Erro ao renovar', description: error.message, status: 'error' });
    }
    setRenovarModal({ isOpen: false, mensalista: null, dataInicio: '', dataFim: '' });
  };
  
  // Handler para ativar mensalista pendente
  const handleAtivarPendente = async (mensalista) => {
    if (!window.confirm(`Deseja ativar o mensalista ${mensalista.nome}?`)) return;
    
    // Obter data atual e data de fim (30 dias depois)
    const hoje = getHojeLocalISO();
    const fim = getFimLocalISO(30);
    
    const { error } = await supabase.from('mensalistas').update({
      status: 'ativo',
      data_inicio: hoje,
      data_fim: fim,
    }).eq('id', mensalista.id);
    
    if (!error) {
      toast({ title: 'Mensalista ativado com sucesso!', status: 'success' });
      fetchMensalistas();
    } else {
      toast({ title: 'Erro ao ativar mensalista', description: error.message, status: 'error' });
    }
  };

  // Handler para fechar modal
  const handleCloseRenovar = () => {
    setRenovarModal({ isOpen: false, mensalista: null, dataInicio: '', dataFim: '' });
  };


  const fetchMensalistas = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('mensalistas').select('*').order('created_at', { ascending: false });
    if (!error) setMensalistas(data);
    setLoading(false);
  };

  useEffect(() => { fetchMensalistas(); fetchBarbeiros(); fetchPacotes(); }, []);

  // Buscar pacotes
  const fetchPacotes = async () => {
    const { data, error } = await supabase.from('pacotes_mensalistas').select('id, nome, valor_mensal').order('nome');
    if (!error) setPacotes(data);
  };

  // Buscar barbeiros
  const fetchBarbeiros = async () => {
    const { data, error } = await supabase.from('barbeiros').select('id, nome').order('nome');
    if (!error) setBarbeiros(data);
  };

  // Atualiza mensalistas vencidos no Supabase
  useEffect(() => {
    const atualizarVencidos = async () => {
      const hoje = new Date();
      let houveMudanca = false;
      for (const m of mensalistas) {
        const dataFim = m.data_fim ? new Date(m.data_fim) : null;
        if (dataFim && dataFim < hoje && m.status !== 'inativo') {
          await supabase.from('mensalistas').update({ status: 'inativo' }).eq('id', m.id);
          houveMudanca = true;
        }
      }
      if (houveMudanca) fetchMensalistas();
    };
    if (mensalistas.length > 0) atualizarVencidos();
    // eslint-disable-next-line
  }, [mensalistas]);

  const openModal = (mensalista = null) => {
    if (mensalista) {
      setForm({ ...mensalista, data_inicio: mensalista.data_inicio ? mensalista.data_inicio.slice(0, 10) : '', data_fim: mensalista.data_fim ? mensalista.data_fim.slice(0, 10) : '' });
      setEditingId(mensalista.id);
    } else {
      setForm(initialForm);
      setEditingId(null);
    }
    onOpen();
  };

  const handleSave = async () => {
    if (!form.nome || !form.data_inicio || !form.telefone || !form.barbeiro_id) {
      toast({ title: 'Nome, telefone e data de início são obrigatórios', status: 'warning' });
      return;
    }
    let fotoUrl = form.foto_url;
    if (fotoFile) {
      setUploadingFoto(true);
      const ext = fotoFile.name.split('.').pop();
      const fileName = `${form.nome.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('mensalistas').upload(fileName, fotoFile, { upsert: true });
      if (uploadError) {
        toast({ title: 'Erro ao enviar foto', description: uploadError.message, status: 'error' });
        setUploadingFoto(false);
        return;
      }
      // Pega a URL pública
      const { data: publicUrlData } = supabase.storage.from('mensalistas').getPublicUrl(fileName);
      fotoUrl = publicUrlData.publicUrl;
      setUploadingFoto(false);
    }
    let result;
    const formToSave = { ...form, foto_url: fotoUrl };
    if (editingId) {
      result = await supabase.from('mensalistas').update(formToSave).eq('id', editingId);
    } else {
      result = await supabase.from('mensalistas').insert([formToSave]);
    }
    if (!result.error) {
      toast({ title: 'Mensalista salvo com sucesso', status: 'success' });
      fetchMensalistas();
      onClose();
      setFotoFile(null);
    } else {
      toast({ title: 'Erro ao salvar mensalista', description: result.error.message, status: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este mensalista?')) return;
    const { error } = await supabase.from('mensalistas').delete().eq('id', id);
    if (!error) {
      toast({ title: 'Mensalista excluído', status: 'info' });
      fetchMensalistas();
    } else {
      toast({ title: 'Erro ao excluir', description: error.message, status: 'error' });
    }
  };

  return (
    <AdminLayout>
      <Box mt={{ base: 6, md: 0 }}>
        <HStack justify="space-between" mb={8}>
          <Heading size="lg">Mensalistas</Heading>
          <Button leftIcon={<AddIcon />} colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => openModal()}>Adicionar Mensalista</Button>
        </HStack>
        {loading ? <Spinner /> : (
          <Box>
            {/* Desktop: tabela, Mobile: cards */}
            <Box display={{ base: 'none', md: 'block' }} overflowX="auto">
              <Table variant="striped" colorScheme="blackAlpha">
                <Thead>
                  <Tr>
                    <Th>Nome</Th>
                    <Th>Telefone</Th>
                    <Th>Início</Th>
                    <Th>Fim</Th>
                    <Th>Status</Th>
                    <Th>Barbeiro</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {mensalistas.map(m => {

                    return (
                      <Tr key={m.id}>
                        <Td>{m.nome}</Td>
                        <Td>{formatTelefoneBR(m.telefone)}</Td>
                        <Td>{formatarDataBR(m.data_inicio)}</Td>
                        <Td>{formatarDataBR(m.data_fim)}</Td>
                        <Td>{m.status}</Td>
                        <Td>{barbeiros.find(b => b.id === m.barbeiro_id)?.nome || '-'}</Td>
                        <Td>
                          <IconButton icon={<EditIcon />} aria-label="Editar" size="sm" mr={2} colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => openModal(m)} />
                          <IconButton icon={<DeleteIcon />} aria-label="Excluir" size="sm" colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => handleDelete(m.id)} />
                          {m.status === 'inativo' && (
                            <Button
                              size="sm"
                              colorScheme="yellow"
                              bg="yellow.400"
                              color="black"
                              _hover={{ bg: 'yellow.500' }}
                              borderRadius="md"
                              fontWeight="bold"
                              ml={2}
                              onClick={() => handleRenovarClick(m)}
                            >
                              Renovar
                            </Button>
                          )}
                          {m.status === 'pendente' && (
                            <Button
                              size="sm"
                              colorScheme="green"
                              bg="green.500"
                              color="white"
                              _hover={{ bg: 'green.600' }}
                              borderRadius="md"
                              fontWeight="bold"
                              ml={2}
                              onClick={() => handleAtivarPendente(m)}
                            >
                              Ativar
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
            {/* Mobile: cards */}
            <Stack spacing={4} display={{ base: 'flex', md: 'none' }} flexDir="column">
              {mensalistas.map(m => {

                return (
                  <Box key={m.id} borderWidth={1} borderRadius="lg" p={4} mb={4} bg="gray.900" color="white" boxShadow="md">
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontWeight="bold" fontSize="lg">{m.nome}</Text>
                      <HStack>
                        <IconButton icon={<EditIcon />} aria-label="Editar" size="sm" colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => openModal(m)} />
                        <IconButton icon={<DeleteIcon />} aria-label="Excluir" size="sm" colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => handleDelete(m.id)} />
                      </HStack>
                    </Flex>
                    <Text fontSize="sm"><b>Telefone:</b> {formatTelefoneBR(m.telefone)}</Text>
                    <Text fontSize="sm"><b>Início:</b> {formatarDataBR(m.data_inicio)}</Text>
                    <Text fontSize="sm"><b>Fim:</b> {formatarDataBR(m.data_fim)}</Text>
                    <Text fontSize="sm"><b>Status:</b> {m.status}</Text>
                    <Text fontSize="sm"><b>Barbeiro:</b> {barbeiros.find(b => b.id === m.barbeiro_id)?.nome || '-'}</Text>
                    {m.status === 'inativo' && (
                      <HStack mt={2} spacing={2}>
                        <Button size="sm" colorScheme="yellow" bg="yellow.400" color="black" _hover={{ bg: 'yellow.500' }} onClick={() => handleRenovarClick(m)}>
                          Renovar
                        </Button>
                      </HStack>
                    )}
                    {m.status === 'pendente' && (
                      <HStack mt={2} spacing={2}>
                        <Button size="sm" colorScheme="green" bg="green.500" color="white" _hover={{ bg: 'green.600' }} onClick={() => handleAtivarPendente(m)}>
                          Ativar
                        </Button>
                      </HStack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
        {/* Modal de renovação */}
        <Modal isOpen={renovarModal.isOpen} onClose={handleCloseRenovar} size={{ base: "md", md: "lg" }} isCentered>
          <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
          <ModalContent 
            bg="gray.800" 
            color="white" 
            borderRadius="xl" 
            boxShadow="xl"
            mx={{ base: 3, md: 0 }}
            my={{ base: 3, md: 0 }}
            maxH={{ base: "calc(100vh - 80px)", md: "auto" }}
            overflowY="auto"
          >
            <ModalHeader borderBottomWidth="1px" borderColor="gray.700" py={4} px={6}>
              <Flex align="center">
                <EditIcon color="yellow.300" mr={3} />
                <Text fontSize="xl" fontWeight="bold" color="white">
                  Confirmar Renovação
                </Text>
              </Flex>
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody py={6} px={{ base: 4, md: 6 }}>
              <Text color="white">Deseja renovar o plano de <b>{renovarModal.mensalista?.nome}</b>?</Text>
              <Box mt={4} p={4} bg="gray.700" borderRadius="md" borderWidth="1px" borderColor="gray.600">
                <Text mt={1} color="whiteAlpha.900"><b>Nova Data de Início:</b> {formatarDataBR(renovarModal.dataInicio)}</Text>
                <Text mt={2} color="whiteAlpha.900"><b>Nova Data Final:</b> {formatarDataBR(renovarModal.dataFim)}</Text>
              </Box>
            </ModalBody>
            <ModalFooter borderTopWidth="1px" borderColor="gray.700" py={4} px={6}>
              <Flex justify="space-between" w="100%" direction={{ base: 'column', sm: 'row' }} gap={3}>
                <Button 
                  variant="outline" 
                  onClick={handleCloseRenovar} 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ bg: "gray.700" }}
                  width={{ base: "100%", sm: "auto" }}
                >
                  Cancelar
                </Button>
                <Button 
                  colorScheme="green" 
                  onClick={handleConfirmRenovar}
                  width={{ base: "100%", sm: "auto" }}
                  _hover={{ bg: "green.500" }}
                >
                  Confirmar Renovação
                </Button>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Modal de cadastro/edição */}
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "md", md: "xl" }} isCentered>
          <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(5px)" />
          <ModalContent 
            bg="gray.800" 
            color="white" 
            borderRadius="xl" 
            boxShadow="xl"
            mx={{ base: 3, md: 0 }}
            my={{ base: 3, md: 0 }}
            maxH={{ base: "calc(100vh - 80px)", md: "auto" }}
            overflowY="auto"
          >
            <ModalHeader borderBottomWidth="1px" borderColor="gray.700" py={4} px={6}>
              <Flex align="center">
                {editingId ? (
                  <EditIcon color="yellow.300" mr={3} />
                ) : (
                  <AddIcon color="yellow.300" mr={3} />
                )}
                <Text fontSize="xl" fontWeight="bold" color="white">
                  {editingId ? 'Editar Mensalista' : 'Novo Mensalista'}
                </Text>
              </Flex>
            </ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody py={6} px={{ base: 4, md: 6 }}>
              <Box as="form" w="100%">
                <Box
                  display="grid"
                  gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
                  gap={4}
                  mb={4}
                  w="100%"
                >
                  <FormControl gridColumn={{ base: '1', md: '1 / span 2' }}>
                    <FormLabel fontWeight="medium" color="whiteAlpha.900">Foto do Mensalista</FormLabel>
                    <Box 
                      borderWidth="2px" 
                      borderStyle="dashed" 
                      borderColor="gray.600" 
                      borderRadius="md" 
                      p={4} 
                      textAlign="center"
                      _hover={{ borderColor: "yellow.300" }}
                      position="relative"
                      overflow="hidden"
                    >
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={e => setFotoFile(e.target.files[0])}
                        height="100%"
                        width="100%"
                        position="absolute"
                        top="0"
                        left="0"
                        opacity="0"
                        aria-hidden="true"
                        cursor="pointer"
                      />
                      <Text color="whiteAlpha.800" mb={2}>Arraste uma foto ou clique para selecionar</Text>
                      <Button size="sm" colorScheme="yellow" variant="outline" pointerEvents="none">
                        Escolher Arquivo
                      </Button>
                      {fotoFile && (
                        <Text fontSize="sm" color="yellow.300" mt={2}>{fotoFile.name}</Text>
                      )}
                      {form.foto_url && !fotoFile && (
                        <Box mt={2}>
                          <img src={form.foto_url} alt="Foto atual" style={{ maxWidth: 120, borderRadius: 8, margin: '0 auto' }} />
                        </Box>
                      )}
                    </Box>
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" color="whiteAlpha.900">Nome</FormLabel>
                    <Input 
                      value={form.nome} 
                      onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} 
                      bg="gray.700" 
                      color="white" 
                      borderColor="gray.600"
                      _hover={{ borderColor: "yellow.300" }}
                      _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                      placeholder="Nome completo"
                    />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" color="whiteAlpha.900">Telefone</FormLabel>
                    <Input
                      value={form.telefone}
                      onChange={e => setForm(f => ({ ...f, telefone: formatPhoneBR(e.target.value) }))}
                      maxLength={15}
                      placeholder="(99) 99999-9999"
                      bg="gray.700" 
                      color="white" 
                      borderColor="gray.600"
                      _hover={{ borderColor: "yellow.300" }}
                      _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                    />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" color="whiteAlpha.900">Barbeiro</FormLabel>
                    <Select 
                      placeholder="Selecione o barbeiro" 
                      value={form.barbeiro_id} 
                      onChange={e => setForm(f => ({ ...f, barbeiro_id: e.target.value }))} 
                      bg="gray.700" 
                      color="white" 
                      borderColor="gray.600"
                      _hover={{ borderColor: "yellow.300" }}
                      _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                    >
                      {barbeiros.map(b => (
                        <option key={b.id} value={b.id} style={{ background: '#2D3748', color: 'white' }}>{b.nome}</option>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" color="whiteAlpha.900">Plano/Pacote</FormLabel>
                    <Select
                      placeholder="Selecione o pacote"
                      value={form.pacote_id}
                      onChange={e => setForm(f => ({ ...f, pacote_id: e.target.value }))}
                      bg="gray.700" 
                      color="white" 
                      borderColor="gray.600"
                      _hover={{ borderColor: "yellow.300" }}
                      _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                    >
                      {pacotes.map(p => (
                        <option key={p.id} value={p.id} style={{ background: '#2D3748', color: 'white' }}>
                          {p.nome} ({Number(p.valor_mensal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" color="whiteAlpha.900">Data Início</FormLabel>
                    <Input
                      value={form.data_inicio}
                      onChange={e => {
                        const dataInicio = e.target.value;
                        // Calcula data final automaticamente (30 dias depois)
                        let dataFinal = '';
                        if (dataInicio) {
                          const dt = new Date(dataInicio);
                          dt.setDate(dt.getDate() + 30);
                          // Ajusta para formato yyyy-mm-dd
                          dataFinal = dt.toISOString().slice(0, 10);
                        }
                        setForm(f => ({
                          ...f,
                          data_inicio: dataInicio,
                          data_fim: dataInicio ? dataFinal : f.data_fim
                        }));
                      }}
                      type="date"
                      bg="gray.700" 
                      color="white" 
                      borderColor="gray.600"
                      _hover={{ borderColor: "yellow.300" }}
                      _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                    />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontWeight="medium" color="whiteAlpha.900">Data Final</FormLabel>
                    <Input 
                      type="date" 
                      value={form.data_fim} 
                      onChange={e => setForm(f => ({ ...f, data_fim: e.target.value }))} 
                      bg="gray.700" 
                      color="white" 
                      borderColor="gray.600"
                      _hover={{ borderColor: "yellow.300" }}
                      _focus={{ borderColor: "yellow.300", boxShadow: "0 0 0 1px var(--chakra-colors-yellow-300)" }}
                    />
                  </FormControl>
                </Box>
              </Box>
            </ModalBody>
            
            <ModalFooter borderTopWidth="1px" borderColor="gray.700" py={4} px={6}>
              <Flex justify="space-between" w="100%" direction={{ base: 'column', sm: 'row' }} gap={3}>
                <Button 
                  variant="outline" 
                  onClick={onClose} 
                  color="white" 
                  borderColor="gray.600"
                  _hover={{ bg: "gray.700" }}
                  width={{ base: "100%", sm: "auto" }}
                >
                  Cancelar
                </Button>
                <Button 
                  colorScheme="yellow" 
                  onClick={handleSave}
                  leftIcon={editingId ? <EditIcon /> : <AddIcon />}
                  width={{ base: "100%", sm: "auto" }}
                  _hover={{ bg: "yellow.500" }}
                >
                  {editingId ? 'Atualizar Mensalista' : 'Salvar Mensalista'}
                </Button>
              </Flex>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </AdminLayout>
  );
};

export default MensalistasPage;
